/**
 * Standalone promote admin: Cognito Hosted UI (PKCE) + Promote API + team management.
 */

export interface PromoteAdminConfig {
  apiUrl: string;
  userPoolId: string;
  clientId: string;
  cognitoDomain: string;
  redirectUri: string;
}

interface TokenBundle {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
}

interface WorkflowRunView {
  id: number;
  status: string;
  conclusion: string | null;
  url: string;
  inProgress: boolean;
  startedAt: string | null;
  typicalDurationSeconds: number | null;
  estimatedRemainingSeconds: number | null;
}

export interface PromoteStatus {
  devSha: string;
  prodSha: string;
  hasPendingChanges: boolean;
  canFastForward: boolean;
  compareStatus: string;
  message: string;
  promoteRun: WorkflowRunView | null;
  deployRun: WorkflowRunView | null;
  pipelineBusy: boolean;
  canPromote: boolean;
  pipelineEtaSeconds: number | null;
}

export interface TeamUser {
  username: string;
  email: string;
  status: string;
  enabled: boolean;
  role: 'admin' | 'operator' | 'none';
  mfaEnabled: boolean;
  createdAt: string | null;
}

const TOKEN_STORAGE_KEY = 'bsi.promote.tokens';
const PKCE_VERIFIER_KEY = 'bsi.promote.pkce';
const MONITORING_STORAGE_KEY = 'bsi.promote.monitoring';
const ADMINS_GROUP = 'promote-admins';

function encodeUserPathId(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function userPathId(user: TeamUser): string {
  const key = user.email !== '' ? user.email : user.username;
  return encodeUserPathId(key);
}

function sha256Base64Url(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  return crypto.subtle.digest('SHA-256', data).then((hash) => {
    const bytes = new Uint8Array(hash);
    let binary = '';
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  });
}

function randomString(length: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function readTokens(): TokenBundle | null {
  const raw = sessionStorage.getItem(TOKEN_STORAGE_KEY);
  if (raw === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as TokenBundle;
    if (typeof parsed.accessToken !== 'string' || typeof parsed.idToken !== 'string') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeTokens(tokens: TokenBundle): void {
  sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
}

function clearTokens(): void {
  sessionStorage.removeItem(TOKEN_STORAGE_KEY);
}

function cognitoBaseUrl(cognitoDomain: string): string {
  return cognitoDomain.startsWith('http')
    ? cognitoDomain.replace(/\/$/, '')
    : `https://${cognitoDomain}`;
}

function parseIdTokenGroups(idToken: string): string[] {
  const parts = idToken.split('.');
  if (parts.length < 2) {
    return [];
  }
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))) as {
      'cognito:groups'?: unknown;
    };
    const claim = payload['cognito:groups'];
    if (Array.isArray(claim)) {
      return claim.filter((item): item is string => typeof item === 'string');
    }
    if (typeof claim === 'string') {
      return [claim];
    }
  } catch {
    return [];
  }
  return [];
}

function isAdmin(idToken: string): boolean {
  return parseIdTokenGroups(idToken).includes(ADMINS_GROUP);
}

async function beginSignIn(config: PromoteAdminConfig): Promise<void> {
  const verifier = randomString(32);
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  const challenge = await sha256Base64Url(verifier);
  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: 'code',
    scope: 'openid email',
    redirect_uri: config.redirectUri,
    code_challenge_method: 'S256',
    code_challenge: challenge,
  });
  window.location.assign(
    `${cognitoBaseUrl(config.cognitoDomain)}/oauth2/authorize?${params.toString()}`,
  );
}

async function exchangeCode(config: PromoteAdminConfig, code: string): Promise<TokenBundle> {
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  if (verifier === null) {
    throw new Error('Missing PKCE verifier.');
  }
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.clientId,
    code,
    redirect_uri: config.redirectUri,
    code_verifier: verifier,
  });
  const response = await fetch(`${cognitoBaseUrl(config.cognitoDomain)}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!response.ok) {
    throw new Error(`Cognito token exchange failed (${response.status}).`);
  }
  const payload = (await response.json()) as {
    access_token: string;
    id_token: string;
    refresh_token?: string;
    expires_in: number;
  };
  const tokens: TokenBundle = {
    accessToken: payload.access_token,
    idToken: payload.id_token,
    refreshToken: payload.refresh_token ?? '',
    expiresAt: Date.now() + payload.expires_in * 1000,
  };
  writeTokens(tokens);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  return tokens;
}

function apiBase(config: PromoteAdminConfig): string {
  return config.apiUrl.replace(/\/$/, '');
}

async function readApiError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string };
    if (typeof body.error === 'string' && body.error !== '') {
      return body.error;
    }
  } catch {
    // Response body was not JSON.
  }
  return `Request failed (${response.status}).`;
}

async function apiFetch(
  config: PromoteAdminConfig,
  idToken: string,
  path: string,
  init?: RequestInit,
): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(`${apiBase(config)}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${idToken}`,
        ...(init?.headers ?? {}),
      },
    });
  } catch {
    throw new Error(`Could not reach the promote API at ${config.apiUrl}.`);
  }
  if (response.status === 401 || response.status === 403) {
    clearTokens();
    throw new Error('Session expired or not authorized.');
  }
  return response;
}

async function fetchStatus(config: PromoteAdminConfig, idToken: string): Promise<PromoteStatus> {
  const response = await apiFetch(config, idToken, '/promote/status');
  if (!response.ok) {
    throw new Error(`Status request failed (${response.status}).`);
  }
  const payload = (await response.json()) as PromoteStatus;
  return {
    ...payload,
    canPromote: payload.canPromote === true,
  };
}

async function triggerPromote(config: PromoteAdminConfig, idToken: string): Promise<void> {
  const response = await apiFetch(config, idToken, '/promote', { method: 'POST' });
  if (response.status === 409) {
    throw new Error('Promote is not available right now.');
  }
  if (!response.ok && response.status !== 202) {
    throw new Error(await readApiError(response));
  }
}

async function fetchUsers(config: PromoteAdminConfig, idToken: string): Promise<TeamUser[]> {
  const response = await apiFetch(config, idToken, '/admin/users');
  if (!response.ok) {
    throw new Error(await readApiError(response));
  }
  const payload = (await response.json()) as { users?: TeamUser[] };
  return Array.isArray(payload.users) ? payload.users : [];
}

async function inviteUser(
  config: PromoteAdminConfig,
  idToken: string,
  email: string,
  role: 'admin' | 'operator',
): Promise<TeamUser> {
  const response = await apiFetch(config, idToken, '/admin/users', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, role }),
  });
  if (!response.ok) {
    throw new Error(await readApiError(response));
  }
  const payload = (await response.json()) as { user: TeamUser };
  return payload.user;
}

async function userAction(
  config: PromoteAdminConfig,
  idToken: string,
  username: string,
  action: string,
  body?: unknown,
): Promise<TeamUser> {
  const response = await apiFetch(
    config,
    idToken,
    `/admin/users/${encodeURIComponent(username)}/${action}`,
    {
      method: 'POST',
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    },
  );
  if (!response.ok) {
    throw new Error(await readApiError(response));
  }
  const payload = (await response.json()) as { user: TeamUser };
  return payload.user;
}

async function removeUser(
  config: PromoteAdminConfig,
  idToken: string,
  username: string,
): Promise<void> {
  const response = await apiFetch(config, idToken, `/admin/users/${encodeURIComponent(username)}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(await readApiError(response));
  }
}

function formatPipeline(status: PromoteStatus, awaitingDispatch = false): string {
  if (awaitingDispatch) {
    return 'Promote workflow starting…';
  }
  if (status.promoteRun !== null && status.promoteRun.inProgress) {
    return `Promote workflow ${status.promoteRun.status} — ${status.promoteRun.url}`;
  }
  if (status.deployRun !== null && status.deployRun.inProgress) {
    return `Prod deploy ${status.deployRun.status} — ${status.deployRun.url}`;
  }
  if (status.canPromote) {
    return 'Idle — ready to promote.';
  }
  return status.message;
}

function formatEta(seconds: number | null): string | null {
  if (seconds === null || seconds <= 0) {
    return null;
  }
  if (seconds < 90) {
    return `~${seconds}s remaining`;
  }
  const minutes = Math.ceil(seconds / 60);
  if (minutes === 1) {
    return '~1 min remaining';
  }
  return `~${minutes} min remaining`;
}

function shortSha(sha: string): string {
  return sha.length >= 7 ? sha.slice(0, 7) : sha;
}

function formatUserStatus(status: string): string {
  if (status === 'FORCE_CHANGE_PASSWORD') {
    return 'Invited';
  }
  if (status === 'CONFIRMED') {
    return 'Confirmed';
  }
  return status;
}

function initTeamPanel(config: PromoteAdminConfig, getIdToken: () => string | null): void {
  const panel = document.getElementById('team-panel');
  const form = document.getElementById('invite-form');
  const emailInput = document.getElementById('invite-email');
  const roleSelect = document.getElementById('invite-role');
  const inviteSubmit = document.getElementById('invite-submit');
  const teamMessage = document.getElementById('team-message');
  const table = document.getElementById('users-table');
  const tbody = document.getElementById('users-table-body');

  if (
    panel === null ||
    !(form instanceof HTMLFormElement) ||
    !(emailInput instanceof HTMLInputElement) ||
    !(roleSelect instanceof HTMLSelectElement) ||
    !(inviteSubmit instanceof HTMLButtonElement) ||
    teamMessage === null ||
    table === null ||
    tbody === null
  ) {
    return;
  }

  const showTeamMessage = (text: string, isError: boolean): void => {
    teamMessage.hidden = false;
    teamMessage.textContent = text;
    teamMessage.style.color = isError ? '#a22' : '';
  };

  const renderUsers = (users: TeamUser[]): void => {
    tbody.replaceChildren();
    if (users.length === 0) {
      table.hidden = true;
      return;
    }
    table.hidden = false;
    for (const user of users) {
      const row = document.createElement('tr');

      const emailCell = document.createElement('td');
      emailCell.textContent = user.email;

      const statusCell = document.createElement('td');
      statusCell.textContent = formatUserStatus(user.status);

      const roleCell = document.createElement('td');
      roleCell.textContent =
        user.role === 'admin' ? 'Admin' : user.role === 'operator' ? 'Operator' : '—';

      const mfaCell = document.createElement('td');
      mfaCell.textContent = user.mfaEnabled ? 'Yes' : 'No';

      const enabledCell = document.createElement('td');
      enabledCell.textContent = user.enabled ? 'Yes' : 'No';

      const actionsCell = document.createElement('td');
      const actions = document.createElement('div');
      actions.className = 'row-actions';

      const addAction = (label: string, handler: () => void, danger = false): void => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = label;
        if (danger) {
          button.className = 'btn--danger';
        }
        button.addEventListener('click', () => {
          void handler();
        });
        actions.appendChild(button);
      };

      const idToken = getIdToken();
      if (idToken === null) {
        return;
      }

      const reload = (): void => {
        void refreshTeam();
      };

      const pathId = userPathId(user);

      if (user.role !== 'operator') {
        addAction('Make operator', () => {
          void userAction(config, idToken, pathId, 'role', { role: 'operator' })
            .then(reload)
            .catch((error: unknown) => {
              showTeamMessage(error instanceof Error ? error.message : 'Role change failed.', true);
            });
        });
      }
      if (user.role !== 'admin') {
        addAction('Make admin', () => {
          void userAction(config, idToken, pathId, 'role', { role: 'admin' })
            .then(reload)
            .catch((error: unknown) => {
              showTeamMessage(error instanceof Error ? error.message : 'Role change failed.', true);
            });
        });
      }

      if (user.status === 'FORCE_CHANGE_PASSWORD') {
        addAction('Resend invite', () => {
          void userAction(config, idToken, pathId, 'resend-invite')
            .then(reload)
            .catch((error: unknown) => {
              showTeamMessage(error instanceof Error ? error.message : 'Resend failed.', true);
            });
        });
      }

      if (user.enabled) {
        addAction('Disable', () => {
          if (!window.confirm(`Disable ${user.email}?`)) {
            return;
          }
          void userAction(config, idToken, pathId, 'disable')
            .then(reload)
            .catch((error: unknown) => {
              showTeamMessage(error instanceof Error ? error.message : 'Disable failed.', true);
            });
        });
      } else {
        addAction('Enable', () => {
          void userAction(config, idToken, pathId, 'enable')
            .then(reload)
            .catch((error: unknown) => {
              showTeamMessage(error instanceof Error ? error.message : 'Enable failed.', true);
            });
        });
      }

      addAction(
        'Remove',
        () => {
          if (!window.confirm(`Remove ${user.email}? This cannot be undone.`)) {
            return;
          }
          void removeUser(config, idToken, pathId)
            .then(reload)
            .catch((error: unknown) => {
              showTeamMessage(error instanceof Error ? error.message : 'Remove failed.', true);
            });
        },
        true,
      );

      actionsCell.appendChild(actions);
      row.append(emailCell, statusCell, roleCell, mfaCell, enabledCell, actionsCell);
      tbody.appendChild(row);
    }
  };

  const refreshTeam = async (): Promise<void> => {
    const idToken = getIdToken();
    if (idToken === null || !isAdmin(idToken)) {
      panel.hidden = true;
      return;
    }
    panel.hidden = false;
    try {
      const users = await fetchUsers(config, idToken);
      renderUsers(users);
      teamMessage.hidden = true;
    } catch (error) {
      showTeamMessage(error instanceof Error ? error.message : 'Could not load team.', true);
    }
  };

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const idToken = getIdToken();
    if (idToken === null) {
      showTeamMessage('Sign in before inviting.', true);
      return;
    }
    const email = emailInput.value.trim();
    const role = roleSelect.value === 'admin' ? 'admin' : 'operator';
    inviteSubmit.disabled = true;
    void inviteUser(config, idToken, email, role)
      .then(() => {
        emailInput.value = '';
        showTeamMessage(`Invitation sent to ${email}.`, false);
        return refreshTeam();
      })
      .catch((error: unknown) => {
        showTeamMessage(error instanceof Error ? error.message : 'Invite failed.', true);
      })
      .finally(() => {
        inviteSubmit.disabled = false;
      });
  });

  void refreshTeam();
  window.setInterval(() => {
    void refreshTeam();
  }, 30_000);
}

export function initPromoteAdmin(config: PromoteAdminConfig): void {
  const signInButton = document.getElementById('promote-sign-in');
  const submitButton = document.getElementById('promote-submit');
  const lead = document.getElementById('promote-status-lead');
  const detail = document.getElementById('promote-status-detail');
  const devSha = document.getElementById('promote-dev-sha');
  const prodSha = document.getElementById('promote-prod-sha');
  const pending = document.getElementById('promote-pending');
  const pipeline = document.getElementById('promote-pipeline');
  const eta = document.getElementById('promote-eta');

  if (
    !(signInButton instanceof HTMLButtonElement) ||
    !(submitButton instanceof HTMLButtonElement) ||
    lead === null ||
    detail === null ||
    devSha === null ||
    prodSha === null ||
    pending === null ||
    pipeline === null ||
    eta === null
  ) {
    return;
  }

  if (
    config.apiUrl === '' ||
    config.clientId === '' ||
    config.userPoolId === '' ||
    config.cognitoDomain === ''
  ) {
    lead.textContent = 'Promote API or Cognito is not configured for this host.';
    submitButton.disabled = true;
    submitButton.setAttribute('aria-disabled', 'true');
    return;
  }

  let pollTimer: number | undefined;
  let promoteAllowed = false;
  let statusLoading = false;
  let promoteInFlight = false;
  let monitoringPipeline = sessionStorage.getItem(MONITORING_STORAGE_KEY) === 'true';

  const getIdToken = (): string | null => readTokens()?.idToken ?? null;

  const setMonitoring = (active: boolean): void => {
    monitoringPipeline = active;
    if (active) {
      sessionStorage.setItem(MONITORING_STORAGE_KEY, 'true');
      return;
    }
    sessionStorage.removeItem(MONITORING_STORAGE_KEY);
  };

  initTeamPanel(config, getIdToken);

  const stopPolling = (): void => {
    if (pollTimer !== undefined) {
      window.clearInterval(pollTimer);
      pollTimer = undefined;
    }
  };

  const startPolling = (): void => {
    if (pollTimer !== undefined) {
      return;
    }
    pollTimer = window.setInterval(() => {
      void refresh();
    }, 10_000);
  };

  const syncPolling = (status: PromoteStatus): void => {
    if (monitoringPipeline || status.pipelineBusy) {
      startPolling();
      return;
    }
    stopPolling();
  };

  const setSubmitEnabled = (enabled: boolean): void => {
    promoteAllowed = enabled;
    const interactive = enabled && !statusLoading && !promoteInFlight && !monitoringPipeline;
    submitButton.disabled = !interactive;
    submitButton.setAttribute('aria-disabled', interactive ? 'false' : 'true');
  };

  const syncSignInLabel = (): void => {
    signInButton.textContent = readTokens() !== null ? 'Sign out' : 'Sign in';
  };

  const renderStatus = (status: PromoteStatus, signedIn: boolean): void => {
    detail.hidden = false;
    devSha.textContent = shortSha(status.devSha);
    prodSha.textContent = shortSha(status.prodSha);
    pending.textContent = status.hasPendingChanges ? 'Yes' : 'No';
    pipeline.textContent = formatPipeline(status, monitoringPipeline && !status.pipelineBusy);
    const etaLabel = formatEta(status.pipelineEtaSeconds);
    eta.textContent = etaLabel ?? '—';

    if (!signedIn) {
      setSubmitEnabled(false);
      return;
    }

    const canSubmit =
      status.canPromote === true && !monitoringPipeline && status.pipelineBusy !== true;
    setSubmitEnabled(canSubmit);
    if (monitoringPipeline) {
      lead.textContent = status.pipelineBusy
        ? formatPipeline(status)
        : 'Waiting for promote workflow to appear…';
      return;
    }
    lead.textContent = status.canPromote ? 'Ready to promote dev to production.' : status.message;
  };

  const refresh = async (): Promise<void> => {
    const tokens = readTokens();
    if (tokens === null) {
      lead.textContent = 'Sign in to load branch and deployment status.';
      signInButton.textContent = 'Sign in';
      setSubmitEnabled(false);
      detail.hidden = true;
      stopPolling();
      return;
    }

    statusLoading = true;
    setSubmitEnabled(false);
    syncSignInLabel();

    try {
      const status = await fetchStatus(config, tokens.idToken);
      statusLoading = false;
      renderStatus(status, true);

      if (monitoringPipeline && !status.pipelineBusy && !status.hasPendingChanges) {
        setMonitoring(false);
        lead.textContent = 'Production is up to date with dev.';
      }

      syncPolling(status);
    } catch (error) {
      statusLoading = false;
      lead.textContent = error instanceof Error ? error.message : 'Could not load status.';
      syncSignInLabel();
      setSubmitEnabled(false);
      detail.hidden = true;
      stopPolling();
      setMonitoring(false);
    }
  };

  signInButton.addEventListener('click', () => {
    if (readTokens() !== null) {
      clearTokens();
      setMonitoring(false);
      stopPolling();
      void refresh();
      return;
    }
    void beginSignIn(config);
  });

  submitButton.addEventListener('click', () => {
    const tokens = readTokens();
    if (tokens === null) {
      lead.textContent = 'Sign in before promoting.';
      return;
    }
    if (!promoteAllowed) {
      lead.textContent = 'Promote is not available right now.';
      return;
    }

    promoteInFlight = true;
    setSubmitEnabled(false);
    lead.textContent = 'Dispatching promote workflow…';
    void triggerPromote(config, tokens.idToken)
      .then(() => {
        setMonitoring(true);
        lead.textContent = 'Promote workflow dispatched. Monitoring pipeline…';
        startPolling();
      })
      .catch((error: unknown) => {
        lead.textContent = error instanceof Error ? error.message : 'Promote failed.';
        setMonitoring(false);
      })
      .finally(() => {
        promoteInFlight = false;
        void refresh();
      });
  });

  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (code !== null) {
    lead.textContent = 'Completing sign-in…';
    void exchangeCode(config, code)
      .then(() => {
        window.history.replaceState({}, document.title, config.redirectUri);
        return refresh();
      })
      .catch((error: unknown) => {
        lead.textContent = error instanceof Error ? error.message : 'Sign-in failed.';
      });
    return;
  }

  setSubmitEnabled(false);
  if (monitoringPipeline) {
    startPolling();
  }
  void refresh();
}
